/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from 'react-query';
import { api } from '../constants';
import { AxiosInstance } from '../axios.config';
import { IGetOrderParams } from '../manageProduct';
import {
  ICancellationReasonFormData,
  IGetAllOrdersResponse,
  ProductCount,
  StatusCountItem,
} from './types';

const getAllOrder = async (
  params: IGetOrderParams & { status?: string },
): Promise<IGetAllOrdersResponse> => {
  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value != null && value !== '' && value !== undefined,
    ),
  );

  const response = await AxiosInstance.get(api.order.allOrder, {
    params: filteredParams,
  });

  return response.data;
};

export const useGetAllOrder = (params: IGetOrderParams & { status?: string }) => {
  return useQuery<IGetAllOrdersResponse>({
    queryKey: ['allOrder', params],
    queryFn: () => getAllOrder(params),
    keepPreviousData: true,
  });
};

const getStatusCounts = async (): Promise<StatusCountItem[]> => {
  const { data } = await AxiosInstance.get(api.order.statusCount);
  return data;
};

export const useGetStatusCounts = () => {
  return useQuery<StatusCountItem[], Error>({
    queryKey: ['orderStatusCounts'],
    queryFn: getStatusCounts,
    staleTime: 60_000,
  });
};

const moveStatus = async (orderId: string, newStatus: string) => {
  const { data } = await AxiosInstance.post(api.order.moveStatus, {
    order_id: orderId,
    new_status: newStatus,
  });
  return data;
};

export const useMoveStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, newStatus }: { orderId: string; newStatus: string }) =>
      moveStatus(orderId, newStatus),

    onSuccess: () => {
      queryClient.invalidateQueries(['allOrder']);
      queryClient.invalidateQueries(['orderStatusCounts']);
      queryClient.invalidateQueries(['pendingOrder']);
      queryClient.invalidateQueries({ queryKey: ['groupOrder'] }); // all group orders
      queryClient.invalidateQueries({ queryKey: ['orderDetail'] }); // all single order details
      queryClient.invalidateQueries({ queryKey: ['maintenanceImagesByBillId'] }); // new images by bill_id
      queryClient.invalidateQueries(['allPaymentHistory']);
    },
  });
};

const GetOrderById = async (orderId: string): Promise<any> => {
  const response = await AxiosInstance.get(`${api.order.allOrder}${orderId}/`);
  return response.data;
};

export const useGetOrderById = (orderId: string) => {
  return useQuery({
    queryKey: ['orderDetail', orderId],
    queryFn: () => GetOrderById(orderId),
    enabled: !!orderId,
    keepPreviousData: true,
  });
};

const getOrderCancellationReasons = async (): Promise<
  { id: string; reason: string; is_active: boolean }[]
> => {
  const response = await AxiosInstance.get(api.orderTracking.cancel_reasons);
  return response.data;
};

export const useGetOrderCancellationReasons = () => {
  return useQuery<{ id: string; reason: string; is_active: boolean }[]>({
    queryKey: ['orderCancellationReasons'],
    queryFn: getOrderCancellationReasons,
  });
};

const cancelOrder = async (
  data: ICancellationReasonFormData & {
    order_tracking_id: string;
    amount: number;
  },
) => {
  const response = await AxiosInstance.post(api.orderTracking.cancel_order, data);
  return response.data;
};

export const useCancelOrderMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelOrder,
    onSuccess: () => {
      queryClient.invalidateQueries(['orders']);
    },
  });
};

const getProductCount = async (): Promise<ProductCount> => {
  const { data } = await AxiosInstance.get(api.manageProduct.count);
  return data;
};

export const useGetProductCount = () => {
  return useQuery<ProductCount, Error>({
    queryKey: ['productCount'],
    queryFn: getProductCount,
    staleTime: 60_000,
  });
};

const getAllPaymentHistory = async (params: any) => {
  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value != null && value !== ''),
  );
  const response = await AxiosInstance.get(api.payment.history, {
    params: filteredParams,
  });
  return response.data;
};

export const useGetAllPaymentHistory = (params: {
  page?: number;
  page_size?: number;
  search?: string;
  start_date?: string;
  end_date?: string;
  payment_method?: string;
  status?: boolean | null;
}) => {
  return useQuery({
    queryKey: ['allPaymentHistory', params],
    queryFn: () => getAllPaymentHistory(params),
  });
};

export const useGetGroupOrderById = (id?: string) => {
  return useQuery({
    queryKey: ['groupOrder', id],
    queryFn: async () => {
      const result = await getGroupOrderById(id!);
      return result;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    onError: (err) => {
      console.error('Failed to fetch group order:', err);
    },
  });
};

const getGroupOrderById = async (id: string) => {
  const response = await AxiosInstance.get(`${api.payment.history}${id}/`);
  return response.data;
};

const uploadMaintenanceImage = async (formData: FormData) => {
  const { data } = await AxiosInstance.post(api.order.uploadMaintenanceImage, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
};

export const useUploadMaintenanceImage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadMaintenanceImage,
    onSuccess: () => {
      queryClient.invalidateQueries(['allOrder']);
      queryClient.invalidateQueries({ queryKey: ['groupOrder'] });
      queryClient.invalidateQueries({ queryKey: ['orderDetail'] });
      queryClient.invalidateQueries({ queryKey: ['maintenanceImagesByBillId'] });
    },
  });
};

const getMaintenanceImages = async (orderId: string): Promise<any[]> => {
  const response = await AxiosInstance.get(api.order.maintenanceImages(orderId));
  return response.data;
};

export const useGetMaintenanceImages = (orderId: string) => {
  return useQuery({
    queryKey: ['maintenanceImages', orderId],
    queryFn: () => getMaintenanceImages(orderId),
    enabled: !!orderId,
    staleTime: 5 * 60 * 1000,
  });
};

const getMaintenanceImagesByBillId = async (billId: string): Promise<any[]> => {
  const response = await AxiosInstance.get(api.order.maintenanceImagesByTransaction(billId));
  return response.data;
};

export const useGetMaintenanceImagesByBillId = (
  billId: string,
  options?: UseQueryOptions<any[], Error>,
) => {
  return useQuery({
    queryKey: ['maintenanceImagesByBillId', billId],
    queryFn: () => getMaintenanceImagesByBillId(billId),
    staleTime: 5 * 60 * 1000,
    ...options, // This allows overriding enabled, etc.
    enabled: !!billId && options?.enabled !== false, // Respect user override
  });
};
